import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Tenants from './pages/Tenants';
import TenantProducts from './pages/TenantProducts';
import EmailConfig from './pages/EmailConfig';
import WhatsAppConfig from './pages/WhatsAppConfig';
import WhatsAppNotifications from './pages/WhatsAppNotifications';
import OAuthConfig from './pages/OAuthConfig';
import PaymentProviders from './pages/PaymentProviders';
import Payments from './pages/Payments';
import Plans from './pages/Plans';
import Subscriptions from './pages/Subscriptions';
import Revenue from './pages/Revenue';
import PromoCodes from './pages/PromoCodes';
import Leads from './pages/Leads';
import Invoices from './pages/Invoices';
import BillingSettings from './pages/BillingSettings';
import Partners from './pages/Partners';
import PlatformSettings from './pages/PlatformSettings';
import PartnerDashboard from './pages/partner/PartnerDashboard';
import Customers from './pages/partner/Customers';
import Brand from './pages/partner/Brand';
import Usage from './pages/partner/Usage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PortalProvider, usePortal } from './contexts/PortalContext';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import Layout from './components/Layout';
import LoadingScreen from './components/ui/LoadingScreen';
import './styles/global.css';

function ProtectedRoute({ children, operatorOnly = false, partnerOnly = false }) {
  const { isAuthenticated, loading, isPartnerAdmin } = useAuth();
  const { isPartnerPortal, loading: portalLoading } = usePortal();
  const partnerMode = isPartnerPortal || isPartnerAdmin;

  if (loading || portalLoading) {
    return <LoadingScreen message="Restoring session..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (operatorOnly && partnerMode) {
    return <Navigate to="/" replace />;
  }

  if (partnerOnly && !partnerMode) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  const { isPartnerPortal } = usePortal();
  const { isPartnerAdmin } = useAuth();
  const partnerMode = isPartnerPortal || isPartnerAdmin;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={partnerMode ? <PartnerDashboard /> : <Dashboard />} />
        <Route
          path="products"
          element={<ProtectedRoute operatorOnly><Products /></ProtectedRoute>}
        />
        <Route
          path="partners"
          element={<ProtectedRoute operatorOnly><Partners /></ProtectedRoute>}
        />
        <Route
          path="tenants"
          element={<ProtectedRoute operatorOnly><Tenants /></ProtectedRoute>}
        />
        <Route
          path="tenant-products"
          element={<ProtectedRoute operatorOnly><TenantProducts /></ProtectedRoute>}
        />
        <Route
          path="email-config"
          element={<ProtectedRoute operatorOnly><EmailConfig /></ProtectedRoute>}
        />
        <Route
          path="whatsapp-config"
          element={<ProtectedRoute operatorOnly><WhatsAppConfig /></ProtectedRoute>}
        />
        <Route
          path="whatsapp-notifications"
          element={<ProtectedRoute operatorOnly><WhatsAppNotifications /></ProtectedRoute>}
        />
        <Route
          path="oauth-config"
          element={<ProtectedRoute operatorOnly><OAuthConfig /></ProtectedRoute>}
        />
        <Route
          path="platform-settings"
          element={<ProtectedRoute operatorOnly><PlatformSettings /></ProtectedRoute>}
        />
        <Route
          path="payment-providers"
          element={<ProtectedRoute operatorOnly><PaymentProviders /></ProtectedRoute>}
        />
        <Route
          path="payments"
          element={<ProtectedRoute operatorOnly><Payments /></ProtectedRoute>}
        />
        <Route
          path="plans"
          element={<ProtectedRoute operatorOnly><Plans /></ProtectedRoute>}
        />
        <Route
          path="subscriptions"
          element={<ProtectedRoute operatorOnly><Subscriptions /></ProtectedRoute>}
        />
        <Route
          path="leads"
          element={<ProtectedRoute operatorOnly><Leads /></ProtectedRoute>}
        />
        <Route
          path="revenue"
          element={<ProtectedRoute operatorOnly><Revenue /></ProtectedRoute>}
        />
        <Route
          path="promo-codes"
          element={<ProtectedRoute operatorOnly><PromoCodes /></ProtectedRoute>}
        />
        <Route
          path="invoices"
          element={<ProtectedRoute operatorOnly><Invoices /></ProtectedRoute>}
        />
        <Route
          path="billing-settings"
          element={<ProtectedRoute operatorOnly><BillingSettings /></ProtectedRoute>}
        />
        <Route
          path="customers"
          element={<ProtectedRoute partnerOnly><Customers /></ProtectedRoute>}
        />
        <Route
          path="brand"
          element={<ProtectedRoute partnerOnly><Brand /></ProtectedRoute>}
        />
        <Route
          path="usage"
          element={<ProtectedRoute partnerOnly><Usage /></ProtectedRoute>}
        />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <PortalProvider>
        <AuthProvider>
          <ToastProvider>
            <ConfirmProvider>
              <AppRoutes />
            </ConfirmProvider>
          </ToastProvider>
        </AuthProvider>
      </PortalProvider>
    </Router>
  );
}

export default App;
